import React from 'react';
import takeNumber from '../../../utils/take-number';
import propsComparator from '../../../utils/props-comparator';

import './ClockPart.scss';

export interface ClockPartProps {
  time?: string;
  seprator?: string;
  onTimeChanged?: (hour: number, minute: number, second: number, time: string) => void;
}

export interface ClockPartState {
  hour: number;
  minute: number;
  second: number;
}

class ClockPart extends React.Component<ClockPartProps, ClockPartState> {

  private static MAX_HOUR = 23;
  private static MAX_MINUTE = 59;
  private static MAX_SECOND = 59;
  private static NUMBER_REGEX = /^[0-9\b]+$/;
  private static KEY_CODE_UP = 38;
  private static KEY_CODE_DOWN = 40;

  state = {
    time: this.props.time ?? '00:01:00',
    hour: 0,
    minute: 1,
    second: 0
  }

  constructor(props: ClockPartProps) {
    super(props);
    const parts = this.state.time.split(':');
    if (parts.length !== 3) {
      throw new Error('Time is invalid.');
    }

    const [hour, minute, second] = takeNumber(parts);
    if (hour < ClockPart.MAX_HOUR) {
      this.state.hour = hour;
    }
    if (minute > ClockPart.MAX_MINUTE) {
      this.state.minute = 59;
    }
    if (second > ClockPart.MAX_SECOND) {
      this.state.second = 59;
    }
  }

  render() {
    return (
      <div className="clock-part-component">
        <div className="clock-item ltr">
          <div className="flex-row-container">
            <button onClick={this.handleIncrementHour}><i className="far fa-angle-up"></i></button>
            <button onClick={this.handleIncrementMinute}><i className="far fa-angle-up"></i></button>
            <button onClick={this.handleIncrementSecond}><i className="far fa-angle-up"></i></button>
          </div>
          <div className="flex-row-container">
            <div className="input-container">
              <input
                type="text"
                value={this.state.hour}
                onKeyDown={this.handleHourKeyDown}
                onChange={this.handleHourTextChange} />
            </div>
            <div className="input-container">
              <input
                type="text"
                value={this.state.minute}
                onKeyDown={this.handleMinuteKeyDown}
                onChange={this.handleMinuteTextChange} />
            </div>
            <div className="input-container">
              <input
                type="text"
                value={this.state.second}
                onKeyDown={this.handleSecondKeyDown}
                onChange={this.handleSecondTextChange} />
            </div>
          </div>
          <div className="flex-row-container">
            <button onClick={this.handleDecrementHour}><i className="far fa-angle-down"></i></button>
            <button onClick={this.handleDecrementMinute}><i className="far fa-angle-down"></i></button>
            <button onClick={this.handleDecrementSecond}><i className="far fa-angle-down"></i></button>
          </div>
        </div>
      </div>
    );
  }

  handleHourTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.handleTimePartChange('hour', event.target.value, ClockPart.MAX_HOUR);
  }

  handleMinuteTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.handleTimePartChange('minute', event.target.value, ClockPart.MAX_MINUTE);
  }

  handleSecondTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.handleTimePartChange('second', event.target.value, ClockPart.MAX_SECOND);
  }

  handleTimePartChange = (part: string, value: any, max: number) => {
    value = this.getSanitizedTime(value, max);
    if (value !== null) {
      this.setState({ [part]: Number(value) } as any, this.timeChangeTrigger);
    }
  }

  handleIncrementHour = () => {
    let hour = this.state.hour + 1;
    if (hour === 24) {
      hour = 0;
    }
    this.setState({ hour }, this.timeChangeTrigger);
  }

  handleIncrementMinute = () => {
    let minute = this.state.minute + 1;
    if (minute === 60) {
      minute = 0;
    }
    this.setState({ minute }, this.timeChangeTrigger);
  }

  handleIncrementSecond = () => {
    let second = this.state.second + 1;
    if (second === 60) {
      second = 0;
    }
    this.setState({ second }, this.timeChangeTrigger);
  }

  handleDecrementHour = () => {
    let hour = this.state.hour - 1;
    if (hour === -1) {
      hour = 23;
    }
    this.setState({ hour }, this.timeChangeTrigger);
  }

  handleDecrementMinute = () => {
    let minute = this.state.minute - 1;
    if (minute === -1) {
      minute = 59;
    }
    this.setState({ minute }, this.timeChangeTrigger);
  }

  handleDecrementSecond = () => {
    let second = this.state.second - 1;
    if (second === -1) {
      second = 59;
    }
    this.setState({ second }, this.timeChangeTrigger);
  }

  handleHourKeyDown = (event: React.KeyboardEvent) => {
    switch (event.keyCode) {
      case ClockPart.KEY_CODE_UP:
        return this.handleIncrementHour();
      case ClockPart.KEY_CODE_DOWN:
        return this.handleDecrementHour();
    }
  }

  handleMinuteKeyDown = (event: React.KeyboardEvent) => {
    switch (event.keyCode) {
      case ClockPart.KEY_CODE_UP:
        return this.handleIncrementMinute();
      case ClockPart.KEY_CODE_DOWN:
        return this.handleDecrementMinute();
    }
  }

  handleSecondKeyDown = (event: React.KeyboardEvent) => {
    switch (event.keyCode) {
      case ClockPart.KEY_CODE_UP:
        return this.handleIncrementSecond();
      case ClockPart.KEY_CODE_DOWN:
        return this.handleDecrementSecond();
    }
  }

  timeChangeTrigger = () => {
    const { hour, minute, second } = this.state;
    if (hour === 0 && minute < 1) {
      return this.setState({ hour, minute: 1 }, this.timeChangeTrigger);
    }
    if (this.props.onTimeChanged) {
      const formattedTime = this.formatTime(hour, minute, second);
      this.props.onTimeChanged(hour, minute, second, formattedTime);
    }
  }

  formatTime = (hour: number, minute: number, second: number) => {
    let result = '';
    result += (hour < 10) ? `0${hour}:` : `${hour}:`;
    result += (minute < 10) ? `0${minute}:` : `${minute}:`;
    result += (second < 10) ? `0${second}` : `${second}`;
    return result;
  }

  /** @override */
  componentDidUpdate(prevProps: ClockPartProps) {
    const keys = Object.keys(this.state);
    const update = propsComparator<ClockPartState>(keys, prevProps, this.props);
    if (update) {
      this.setState(update, () => {
        const parts = this.state.time.split(':');
        const [hour, minute, second] = takeNumber(parts);
        const state = this.state;
        if (
          hour !== state.hour ||
          minute !== state.minute ||
          second !== state.second
        ) {
          this.setState({ hour, minute, second });
        }
      });
    }
  }

  private getSanitizedTime = (value: any, max: number) => {
    if (value === '') {
      return 0;
    }
    else if (ClockPart.NUMBER_REGEX.test(value)) {
      return (value <= max) ? value : max;
    }
    return null;
  }

}

export default ClockPart;
